import { ReadonlyVec3, mat3, vec3 } from 'gl-matrix';
import { Grid } from '~/engine/data/common/Grid';
import { trilinearInterpolate } from './trilinearSample';

/**
 * Shift an SDF asymmetrically using morphological
 * dilation/erosion with an ellipsoidal kernel.
 *
 * Builds an asymmetric ellipsoid from the per-direction
 * margins and applies a max filter (expansion) or min
 * filter (contraction) over all kernel offsets at each
 * voxel. This generalizes symmetric {@link shiftSdf}:
 * for equal margins in all directions the result matches
 * a uniform shift.
 *
 * @param sdfArrays Per-slice SDF pixel data (Uint8)
 * @param grid Grid defining size, resolution, and
 *        orientation of the SDF volume
 * @param negMarginMm Margins for negative LPS directions
 *        [Right(-), Anterior(-), Inferior(-)] in mm
 * @param posMarginMm Margins for positive LPS directions
 *        [Left(+), Posterior(+), Superior(+)] in mm
 * @returns Shifted SDF slices in the same encoding
 */
export function applyAsymmetricMarginCpu(
    sdfArrays: Uint8Array[],
    grid: Grid,
    negMarginMm: ReadonlyVec3,
    posMarginMm: ReadonlyVec3
): Uint8Array[] {
    const nx = grid.sizeX;
    const ny = grid.sizeY;
    const resX = grid.resX;
    const resY = grid.resY;
    const resZ = grid.resZ;
    const orientation = grid.getOrientation();
    const nz = sdfArrays.length;
    if (nz === 0) {
        return [];
    }

    const bufA: Uint8Array[] = [];
    const bufB: Uint8Array[] = [];
    for (let z = 0; z < nz; z++) {
        bufA.push(new Uint8Array(sdfArrays[z]));
        bufB.push(new Uint8Array(sdfArrays[z].length));
    }

    const { expansion, contraction } = buildEllipsoids(
        negMarginMm,
        posMarginMm,
        resX,
        resY,
        resZ
    );

    // Convert LPS offsets to voxel-space offsets, split into integer
    // and fractional parts. Integer offsets can use direct array lookup;
    // fractional offsets require trilinear interpolation.
    // Stored as flat typed arrays (stride 3) for cache locality.
    const orientInv = mat3.transpose(mat3.create(), orientation as mat3);
    const toVoxelKernel = (
        lpsKernel: number[][]
    ): { intOffsets: Int32Array; fracOffsets: Float32Array } => {
        const intList: number[] = [];
        const fracList: number[] = [];
        const tmp = vec3.create();
        for (const [dl, dp, ds] of lpsKernel) {
            vec3.set(tmp, dl, dp, ds);
            vec3.transformMat3(tmp, tmp, orientInv);
            const vx = tmp[0] / resX;
            const vy = tmp[1] / resY;
            const vz = tmp[2] / resZ;
            const rx = Math.round(vx);
            const ry = Math.round(vy);
            const rz = Math.round(vz);
            if (
                Math.abs(vx - rx) < 1e-6 &&
                Math.abs(vy - ry) < 1e-6 &&
                Math.abs(vz - rz) < 1e-6
            ) {
                intList.push(rx, ry, rz);
            } else {
                fracList.push(vx, vy, vz);
            }
        }
        return {
            intOffsets: new Int32Array(intList),
            fracOffsets: new Float32Array(fracList),
        };
    };
    const expansionKernel = toVoxelKernel(expansion);
    const contractionKernel = toVoxelKernel(contraction);

    const applyMargin = (
        src: Uint8Array[],
        dst: Uint8Array[],
        expand: boolean
    ) => {
        const { intOffsets, fracOffsets } = expand
            ? expansionKernel
            : contractionKernel;
        for (let z = 0; z < nz; z++) {
            for (let y = 0; y < ny; y++) {
                for (let x = 0; x < nx; x++) {
                    const idx = y * nx + x;
                    let newVal = src[z][idx];
                    // Far from boundary, no change (optimization).
                    if (newVal <= 0 || newVal >= 255) {
                        dst[z][idx] = newVal <= 0 ? 0 : 255;
                        continue;
                    }

                    // Integer offsets: direct array lookup, no interpolation.
                    for (let k = 0; k < intOffsets.length; k += 3) {
                        const ni = expand
                            ? x - intOffsets[k]
                            : x + intOffsets[k];
                        const nj = expand
                            ? y - intOffsets[k + 1]
                            : y + intOffsets[k + 1];
                        const nk = expand
                            ? z - intOffsets[k + 2]
                            : z + intOffsets[k + 2];
                        if (
                            ni < 0 ||
                            ni >= nx ||
                            nj < 0 ||
                            nj >= ny ||
                            nk < 0 ||
                            nk >= nz
                        ) {
                            continue;
                        }
                        const neighborVal = src[nk][nj * nx + ni];
                        if (expand) {
                            if (neighborVal > newVal) {
                                newVal = neighborVal;
                                if (newVal >= 255) {
                                    break;
                                }
                            }
                        } else {
                            if (neighborVal < newVal) {
                                newVal = neighborVal;
                                if (newVal <= 0) {
                                    break;
                                }
                            }
                        }
                    }

                    // Fractional offsets: trilinear interpolation.
                    for (let k = 0; k < fracOffsets.length; k += 3) {
                        const ni = expand
                            ? x - fracOffsets[k]
                            : x + fracOffsets[k];
                        const nj = expand
                            ? y - fracOffsets[k + 1]
                            : y + fracOffsets[k + 1];
                        const nk = expand
                            ? z - fracOffsets[k + 2]
                            : z + fracOffsets[k + 2];
                        if (
                            ni < 0 ||
                            ni >= nx ||
                            nj < 0 ||
                            nj >= ny ||
                            nk < 0 ||
                            nk >= nz
                        ) {
                            continue;
                        }
                        const x0 = Math.floor(ni);
                        const y0 = Math.floor(nj);
                        const z0 = Math.floor(nk);
                        const x1 = Math.min(x0 + 1, nx - 1);
                        const y1 = Math.min(y0 + 1, ny - 1);
                        const z1 = Math.min(z0 + 1, nz - 1);
                        const neighborVal = trilinearInterpolate(
                            src[z0][y0 * nx + x0],
                            src[z0][y0 * nx + x1],
                            src[z0][y1 * nx + x0],
                            src[z0][y1 * nx + x1],
                            src[z1][y0 * nx + x0],
                            src[z1][y0 * nx + x1],
                            src[z1][y1 * nx + x0],
                            src[z1][y1 * nx + x1],
                            ni - x0,
                            nj - y0,
                            nk - z0
                        );
                        if (expand) {
                            if (neighborVal > newVal) {
                                newVal = neighborVal;
                                if (newVal >= 255) {
                                    break;
                                }
                            }
                        } else {
                            if (neighborVal < newVal) {
                                newVal = neighborVal;
                                if (newVal <= 0) {
                                    break;
                                }
                            }
                        }
                    }

                    dst[z][idx] =
                        newVal <= 0
                            ? 0
                            : newVal >= 255
                              ? 255
                              : Math.round(newVal);
                }
            }
        }
    };

    const hasExpansion =
        expansionKernel.intOffsets.length + expansionKernel.fracOffsets.length >
        0;
    const hasContraction =
        contractionKernel.intOffsets.length +
            contractionKernel.fracOffsets.length >
        0;

    // Ping-pong between bufA and bufB, only running passes with
    // non-empty kernels. 'current' always holds the latest result.
    let current = bufA;
    let other = bufB;

    if (hasExpansion) {
        applyMargin(current, other, true);
        [current, other] = [other, current];
    }

    if (hasContraction) {
        applyMargin(current, other, false);
        [current, other] = [other, current];
    }

    return current;
}

export interface EllipsoidKernels {
    /** LPS offsets (mm) for dilation (max filter) */
    expansion: number[][];
    /** LPS offsets (mm) for erosion (min filter) */
    contraction: number[][];
}

/**
 * Build two ellipsoid kernels from asymmetric margins:
 * one for expansion (positive margins → dilation) and
 * one for contraction (negative margins → erosion).
 * For same-sign margins one kernel will be empty.
 *
 * Kernels are in LPS patient space (mm). The caller
 * is responsible for converting to voxel-space offsets
 * using the grid orientation and resolution.
 *
 * @param negMarginMm Margins for -L, -P, -S (mm).
 *        Positive = expand, negative = contract.
 * @param posMarginMm Margins for +L, +P, +S (mm).
 *        Positive = expand, negative = contract.
 * @param resX Voxel spacing X in mm
 * @param resY Voxel spacing Y in mm
 * @param resZ Voxel spacing Z in mm
 * @returns Two arrays of [dL, dP, dS] LPS offsets (mm).
 */
export function buildEllipsoids(
    negMarginMm: ReadonlyVec3,
    posMarginMm: ReadonlyVec3,
    resX: number,
    resY: number,
    resZ: number
): EllipsoidKernels {
    const negExpansion = vec3.fromValues(
        Math.max(negMarginMm[0], 0),
        Math.max(negMarginMm[1], 0),
        Math.max(negMarginMm[2], 0)
    );
    const posExpansion = vec3.fromValues(
        Math.max(posMarginMm[0], 0),
        Math.max(posMarginMm[1], 0),
        Math.max(posMarginMm[2], 0)
    );
    const negContraction = vec3.fromValues(
        Math.max(-negMarginMm[0], 0),
        Math.max(-negMarginMm[1], 0),
        Math.max(-negMarginMm[2], 0)
    );
    const posContraction = vec3.fromValues(
        Math.max(-posMarginMm[0], 0),
        Math.max(-posMarginMm[1], 0),
        Math.max(-posMarginMm[2], 0)
    );

    // buildEllipsoid(neg, pos) generates offsets from -neg to +pos
    // in each LPS axis. The morphological filter samples at
    // (center - offset) for expansion, so a positive offset +N
    // samples in the -LPS direction, finding inside voxels for
    // voxels just past the +LPS boundary — expanding the +LPS side.
    // Therefore the positive extent of the kernel must equal the
    // positive-direction margin and the negative extent must equal
    // the negative-direction margin.
    return {
        expansion: buildEllipsoid(negExpansion, posExpansion, resX, resY, resZ),
        contraction: buildEllipsoid(
            negContraction,
            posContraction,
            resX,
            resY,
            resZ
        ),
    };
}

/**
 * Build the set of LPS offsets inside an asymmetric
 * ellipsoid. All margin values must be non-negative.
 *
 * Enumerates points in LPS space on a regular grid.
 * Zero-margin axes have zero range (single value 0),
 * naturally producing a degenerate (1D/2D) kernel
 * that still maps to valid fractional voxel offsets
 * on oblique grids.
 *
 * Step size is min(smallestNonZeroMargin, minRes) to
 * ensure adequate sampling for sub-voxel margins and
 * at most ~1 voxel spacing between samples.
 *
 * @param negMarginMm Radii for -L, -P, -S (mm, >= 0)
 * @param posMarginMm Radii for +L, +P, +S (mm, >= 0)
 * @param resX Voxel spacing X in mm
 * @param resY Voxel spacing Y in mm
 * @param resZ Voxel spacing Z in mm
 * @returns Array of [dL, dP, dS] offsets in mm.
 *          The origin (0,0,0) is excluded.
 */
export function buildEllipsoid(
    negMarginMm: ReadonlyVec3,
    posMarginMm: ReadonlyVec3,
    resX: number,
    resY: number,
    resZ: number
): number[][] {
    const offsets: number[][] = [];

    for (let i = 0; i < 3; i++) {
        if (negMarginMm[i] < 0 || posMarginMm[i] < 0) {
            throw new Error(
                'buildEllipsoid requires non-negative margin values'
            );
        }
    }

    const allMargins = [
        negMarginMm[0],
        negMarginMm[1],
        negMarginMm[2],
        posMarginMm[0],
        posMarginMm[1],
        posMarginMm[2],
    ];

    const maxR = Math.max(...allMargins);

    if (maxR <= 0) {
        return offsets;
    }

    const minMargin = Math.min(...allMargins.filter((m) => m > 0));
    const minRes = Math.min(resX, resY, resZ);
    const baseStep = Math.min(minMargin, minRes) * 0.5;

    // Per-axis range, step count, and adjusted step.
    // The step is adjusted so that range[i] is exactly
    // nSteps[i] * axisStep[i], guaranteeing the
    // ellipsoid boundary is always sampled.
    // Zero-margin axes get nSteps=0 (single sample
    // at 0).
    const negSteps: number[] = [0, 0, 0];
    const posSteps: number[] = [0, 0, 0];
    const negAxisSteps = [0, 0, 0];
    const posAxisSteps = [0, 0, 0];
    for (let i = 0; i < 3; i++) {
        // Zero-margin axes get 0 steps (single sample only at origin,
        // excluded by the iL===0&&iP===0&&iS===0 check). Non-zero axes
        // get enough steps to guarantee the boundary is sampled exactly.
        negSteps[i] =
            negMarginMm[i] > 0 ? Math.ceil(negMarginMm[i] / baseStep) : 0;
        posSteps[i] =
            posMarginMm[i] > 0 ? Math.ceil(posMarginMm[i] / baseStep) : 0;
        negAxisSteps[i] = negSteps[i] > 0 ? negMarginMm[i] / negSteps[i] : 0;
        posAxisSteps[i] = posSteps[i] > 0 ? posMarginMm[i] / posSteps[i] : 0;
    }

    for (let iS = -negSteps[2]; iS <= posSteps[2]; iS++) {
        const s = iS < 0 ? iS * negAxisSteps[2] : iS * posAxisSteps[2];
        for (let iP = -negSteps[1]; iP <= posSteps[1]; iP++) {
            const p = iP < 0 ? iP * negAxisSteps[1] : iP * posAxisSteps[1];
            for (let iL = -negSteps[0]; iL <= posSteps[0]; iL++) {
                const l = iL < 0 ? iL * negAxisSteps[0] : iL * posAxisSteps[0];
                if (iL === 0 && iP === 0 && iS === 0) {
                    continue;
                }

                // Ellipsoid test: sum_i (lps[i]/r_i)^2 <= 1
                // r=0 with lps[i]=0: skip (degenerate axis)
                // r=0 with lps[i]!=0: outside, reject
                const lps = [l, p, s];
                let sum = 0;
                let inside = true;
                for (let axis = 0; axis < 3; axis++) {
                    const r =
                        lps[axis] >= 0 ? posMarginMm[axis] : negMarginMm[axis];
                    if (r === 0) {
                        // Zero margin in this direction
                        if (lps[axis] !== 0) {
                            inside = false;
                            break;
                        }
                        continue;
                    }
                    const t = lps[axis] / r;
                    sum += t * t;
                }

                if (inside && sum <= 1.0) {
                    offsets.push([l, p, s]);
                }
            }
        }
    }

    return offsets;
}

/**
 * Per-direction radii (mm) of the asymmetric ellipsoid the
 * morphological filter uses as its kernel. negR[i] is how far the
 * kernel reaches in the -axis-i direction; posR[i] is how far in
 * the +axis-i direction. Zero radii on every axis means no kernel.
 */
export interface EllipsoidRadii {
    negR: vec3;
    posR: vec3;
}

export interface EllipsoidRadiiPair {
    expansion: EllipsoidRadii;
    contraction: EllipsoidRadii;
}

/**
 * Decompose the per-direction margin parameters into the two
 * ellipsoids the GPU shader needs: the expansion ellipsoid uses
 * the positive part of each margin component (dilation reach in
 * that direction) and the contraction ellipsoid uses the negative
 * part (erosion reach).
 */
export function ellipsoidRadiiForMargin(
    negMarginMm: ReadonlyVec3,
    posMarginMm: ReadonlyVec3
): EllipsoidRadiiPair {
    return {
        expansion: {
            negR: vec3.fromValues(
                Math.max(negMarginMm[0], 0),
                Math.max(negMarginMm[1], 0),
                Math.max(negMarginMm[2], 0)
            ),
            posR: vec3.fromValues(
                Math.max(posMarginMm[0], 0),
                Math.max(posMarginMm[1], 0),
                Math.max(posMarginMm[2], 0)
            ),
        },
        contraction: {
            negR: vec3.fromValues(
                Math.max(-negMarginMm[0], 0),
                Math.max(-negMarginMm[1], 0),
                Math.max(-negMarginMm[2], 0)
            ),
            posR: vec3.fromValues(
                Math.max(-posMarginMm[0], 0),
                Math.max(-posMarginMm[1], 0),
                Math.max(-posMarginMm[2], 0)
            ),
        },
    };
}
